import React, { useState, useEffect } from "react";
import { Grid, Card, Typography, TextField, Button, Box } from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as yup from "yup";
import { useStyles } from "../../../theme/appStyles";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import CircularProgress from "@mui/material/CircularProgress";

const EditDeposit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      date:    "",
      amount:  "",
      remarks: "",
    },
    validationSchema: yup.object({
      date:   yup.date().required("Date is required"),
      amount: yup.number().required("Amount is required").min(0.01, "Amount must be at least 0.01"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await axios.put(`${API_URL}/deposit/${id}`, {
          date:    values.date,
          amount:  values.amount,
          remarks: values.remarks,
        });
        showMessage("Deposit updated successfully!");
        navigate("/deposit/list");
      } catch {
        showErrorMessage("An error occurred while updating the deposit.");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios
      .get(`${API_URL}/deposit/${id}`)
      .then(r => {
        const d = r.data;
        formik.setValues({
          date:    d.date    || "",
          amount:  d.amount  || "",
          remarks: d.remarks || "",
        });
      })
      .catch(() => showErrorMessage("Failed to fetch deposit data."))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <Grid container justifyContent="space-between" sx={{ borderBottom: 2, padding: 2 }}>
              <Grid item xs={12} pt={2} alignContent="center">
                <Typography className={classes.cardTitle}>Edit Deposit</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <Card>
                  <Grid container spacing={3} sx={{ padding: 3 }}>
                    <Grid item xs={12} sm={7}>
                      <Typography variant="subtitle1">Date</Typography>
                      <TextField
                        type="date"
                        size="small"
                        name="date"
                        value={formik.values.date}
                        onChange={formik.handleChange}
                        error={formik.touched.date && Boolean(formik.errors.date)}
                        helperText={formik.touched.date && formik.errors.date}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      <Typography variant="subtitle1">Deposit Amount</Typography>
                      <TextField
                        placeholder="Enter deposit amount"
                        size="small"
                        type="number"
                        {...formik.getFieldProps("amount")}
                        error={formik.touched.amount && Boolean(formik.errors.amount)}
                        helperText={formik.touched.amount && formik.errors.amount}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      <Typography variant="subtitle1">Remarks</Typography>
                      <TextField
                        placeholder="Enter remarks"
                        size="small"
                        {...formik.getFieldProps("remarks")}
                        multiline
                        rows={4}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12} pt={3}>
                <Card>
                  <Grid container spacing={3} sx={{ padding: 3 }}>
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        className={classes.addButton}
                        startIcon={<CloseIcon />}
                        onClick={() => navigate("/deposit/list")}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5} />
                      <Button
                        variant="contained"
                        className={classes.addButton}
                        startIcon={<SaveAltIcon />}
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : "Update"}
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditDeposit;
